import { useEffect, useState } from 'react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'ljp_accessibility';

type ContrastMode = 'default' | 'high' | 'inverted' | 'high-inverted';
type TextSizeMode = 'small' | 'default' | 'large' | 'larger' | 'extra';
type LineHeightMode = 'default' | 'comfortable' | 'loose';

interface AccessibilityPrefs {
  contrast: ContrastMode;
  textSize: TextSizeMode;
  reduceMotion: boolean;
  highlightLinks: boolean;
  lineHeight: LineHeightMode;
  focusVisible: boolean;
}

const defaultPrefs: AccessibilityPrefs = {
  contrast: 'default',
  textSize: 'default',
  reduceMotion: false,
  highlightLinks: false,
  lineHeight: 'default',
  focusVisible: false,
};

function loadPrefs(): AccessibilityPrefs {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...defaultPrefs, ...(JSON.parse(stored) as Partial<AccessibilityPrefs>) };
  } catch {
    // ignore read issues
  }
  return defaultPrefs;
}

function savePrefs(prefs: AccessibilityPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore write issues
  }
}

function applyPrefs(prefs: AccessibilityPrefs) {
  const html = document.documentElement;
  html.setAttribute('data-contrast', prefs.contrast);
  html.setAttribute('data-text-size', prefs.textSize);
  html.setAttribute('data-reduce-motion', prefs.reduceMotion ? 'on' : 'off');
  html.setAttribute('data-highlight-links', prefs.highlightLinks ? 'on' : 'off');
  html.setAttribute('data-line-height', prefs.lineHeight);
  html.setAttribute('data-focus-visible', prefs.focusVisible ? 'on' : 'off');
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<AccessibilityPrefs>(defaultPrefs);

  useEffect(() => {
    const loaded = loadPrefs();
    setPrefs(loaded);
    applyPrefs(loaded);
  }, []);

  const updatePrefs = (updates: Partial<AccessibilityPrefs>) => {
    const next = { ...prefs, ...updates };
    setPrefs(next);
    applyPrefs(next);
    savePrefs(next);
  };

  return (
    <div className="a11y-widget">
      {open && (
        <div className="a11y-panel" role="dialog" aria-label="Accessibility options">
          <div className="a11y-panel-head">
            <h3>Accessibility</h3>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close accessibility panel">
              ×
            </button>
          </div>

          <div className="a11y-group">
            <p>Contrast</p>
            <div className="a11y-options">
              {(['default', 'high', 'inverted', 'high-inverted'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={prefs.contrast === mode ? 'is-active' : ''}
                  onClick={() => updatePrefs({ contrast: mode })}
                >
                  {mode === 'default' ? 'Default' : mode === 'high' ? 'High' : mode === 'inverted' ? 'Inverted' : 'High + Inv'}
                </button>
              ))}
            </div>
          </div>

          <div className="a11y-group">
            <p>Text size</p>
            <div className="a11y-options">
              {(['small', 'default', 'large', 'larger', 'extra'] as const).map((size) => (
                <button
                  key={size}
                  type="button"
                  className={prefs.textSize === size ? 'is-active' : ''}
                  onClick={() => updatePrefs({ textSize: size })}
                >
                  A
                </button>
              ))}
            </div>
          </div>

          <div className="a11y-group">
            <p>Line spacing</p>
            <div className="a11y-options">
              {(['default', 'comfortable', 'loose'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={prefs.lineHeight === mode ? 'is-active' : ''}
                  onClick={() => updatePrefs({ lineHeight: mode })}
                >
                  {mode === 'default' ? 'Default' : mode === 'comfortable' ? 'Comfort' : 'Loose'}
                </button>
              ))}
            </div>
          </div>

          <label className="a11y-toggle">
            <span>Reduce motion</span>
            <input
              type="checkbox"
              checked={prefs.reduceMotion}
              onChange={() => updatePrefs({ reduceMotion: !prefs.reduceMotion })}
            />
          </label>
          <label className="a11y-toggle">
            <span>Highlight links</span>
            <input
              type="checkbox"
              checked={prefs.highlightLinks}
              onChange={() => updatePrefs({ highlightLinks: !prefs.highlightLinks })}
            />
          </label>
          <label className="a11y-toggle">
            <span>Strong focus outline</span>
            <input
              type="checkbox"
              checked={prefs.focusVisible}
              onChange={() => updatePrefs({ focusVisible: !prefs.focusVisible })}
            />
          </label>
        </div>
      )}

      <button
        type="button"
        className="a11y-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close accessibility options' : 'Open accessibility options'}
        aria-expanded={open}
      >
        <svg className="a11y-trigger-icon" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M9.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0M6 5.5l-4.535-.442A.531.531 0 0 1 1.531 4H14.47a.531.531 0 0 1 .066 1.058L10 5.5V9l.452 6.42a.535.535 0 0 1-1.053.174L8.243 9.97c-.064-.252-.422-.252-.486 0l-1.156 5.624a.535.535 0 0 1-1.053-.174L6 9z" />
        </svg>
      </button>
    </div>
  );
}
